// Menú del usuario: lo secundario del encabezado vive aquí (perfil, ambiente y versión, conexión,
// fecha y hora, atajos, salir). En el encabezado queda solo el nombre; si la conexión falla, el aviso
// sí sale afuera, porque eso no puede quedar escondido.
import { useEffect, useRef, useState } from 'react';
import { CambiarClave } from '../sesion/CambiarClave';
import { useSesion } from '../sesion/SesionContexto';
import { useNombresCortos } from './preferencias';
import { useSalud } from './salud';

/** Commit desplegado (UAT: lo fija el build de la imagen `web`); vacío en desarrollo y E2E. */
const VERSION = import.meta.env['VITE_VERSION'] ?? '';

function useEnLinea() {
  const [enLinea, setEnLinea] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setEnLinea(true);
    const off = () => setEnLinea(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return enLinea;
}

/** Estado de la conexión con el servidor: { ok, texto }. */
export function useConexion() {
  const enLinea = useEnLinea();
  const salud = useSalud();
  // Un 429 (límite de peticiones) con una respuesta buena anterior NO es una caída: el servidor
  // respondió; solo pidió esperar.
  const limitado =
    salud.isError &&
    salud.data !== undefined &&
    (salud.error as { status?: number }).status === 429;
  const ok = enLinea && salud.data?.estado === 'ok' && (!salud.isError || limitado);
  const texto = !enLinea
    ? 'Sin red'
    : salud.isError && !limitado
      ? 'Sin servidor'
      : salud.isPending
        ? 'Conectando…'
        : ok
          ? 'En línea'
          : 'Degradado';
  return { ok, pendiente: enLinea && salud.isPending, texto, ambiente: salud.data?.ambiente };
}

/** Aviso en el encabezado SOLO cuando la conexión no está bien. */
export function AvisoConexion() {
  const c = useConexion();
  if (c.ok || c.pendiente) return null;
  return (
    <span className="aviso-conexion" role="status" aria-label={`Conexión: ${c.texto}`}>
      <span className="punto" aria-hidden /> {c.texto}
    </span>
  );
}

function Reloj() {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const f = ahora
    .toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .replaceAll('/', '-');
  const h = ahora.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  return (
    <b className="reloj">
      {f} {h}
    </b>
  );
}

export function MenuUsuario({ alAyuda }: { alAyuda: () => void }) {
  const sesion = useSesion();
  const c = useConexion();
  const [cortos, setCortos] = useNombresCortos();
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto]);
  const [cambiandoClave, setCambiandoClave] = useState(false);
  const nombre = sesion.usuario?.nombre ?? '';
  const iniciales = nombre
    .replace(/\(.*?\)/g, '')
    .split(/\s+/)
    .filter((p) => /^\p{L}/u.test(p))
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return (
    <div className="menu-usuario" ref={caja}>
      <button
        type="button"
        className="boton-usuario"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={`Usuario: ${nombre}`}
        onClick={() => setAbierto(!abierto)}
      >
        <span className={`avatar${c.ok ? '' : ' sin-conexion'}`} aria-hidden>
          {iniciales || '·'}
        </span>
        <span className="nombre-usuario">{nombre}</span>
      </button>
      {abierto && (
        <div className="desplegable-usuario" role="menu" aria-label="Menú del usuario">
          <dl className="datos-usuario">
            <dt>Usuario</dt>
            <dd>
              <b>{nombre}</b>
            </dd>
            <dt>Perfil</dt>
            <dd>{sesion.roles.join(', ') || 'sin rol en esta empresa'}</dd>
            <dt>Empresa</dt>
            <dd>{sesion.empresa?.razon_social}</dd>
            <dt>Conexión</dt>
            <dd>
              <span className={`conexion ${c.ok ? 'ok' : 'mal'}`}>
                <span className="punto" aria-hidden /> {c.texto}
              </span>
            </dd>
            {c.ambiente && (
              <>
                <dt>Ambiente</dt>
                <dd>{c.ambiente.toUpperCase()}</dd>
              </>
            )}
            {VERSION && (
              <>
                <dt>Versión</dt>
                <dd title="Commit desplegado: anótalo en el feedback">{VERSION}</dd>
              </>
            )}
            <dt>Fecha y hora</dt>
            <dd>
              <Reloj />
            </dd>
          </dl>
          <fieldset className="opcion-nombres">
            <legend>Nombres del menú</legend>
            <label>
              <input type="radio" checked={!cortos} onChange={() => setCortos(false)} /> Clásicos
              (Pago de Clientes)
            </label>
            <label>
              <input type="radio" checked={cortos} onChange={() => setCortos(true)} /> Cortos
              (Cobros, Pagos)
            </label>
          </fieldset>
          <div className="acciones-usuario">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setAbierto(false);
                alAyuda();
              }}
            >
              Atajos de teclado <kbd>F1</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setAbierto(false);
                setCambiandoClave(true);
              }}
            >
              Cambiar mi clave
            </button>
            <button type="button" role="menuitem" onClick={() => void sesion.cerrar()}>
              Salir
            </button>
          </div>
        </div>
      )}
      {cambiandoClave && <CambiarClave alCerrar={() => setCambiandoClave(false)} />}
    </div>
  );
}
